import { useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Logo } from "@/components/Logo";
import { Newspaper, Mail, Copy } from "lucide-react";
import { toast } from "sonner";
import { PRESS_RELEASES } from "@/config/pressReleases";

/**
 * Hidden press page.
 *
 * Reachable only by knowing the exact URL — no link from any nav, footer or
 * marketing page, and every render injects `noindex, nofollow, noarchive`
 * so search engines never surface it. Additionally `robots.txt` disallows
 * the entire `/press` prefix.
 *
 * Releases live in `@/config/pressReleases.js` so they ship with the code —
 * one deploy is all it takes for content to go live.
 */
export default function Press() {
  useEffect(() => {
    // Keep this page invisible to crawlers for as long as it's mounted.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => {
      try { document.head.removeChild(meta); } catch { /* already gone */ }
    };
  }, []);

  const copyAnchor = async (slug) => {
    try {
      const url = `${window.location.origin}/press#${slug}`;
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Clipboard blocked — copy the URL manually");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        day: "numeric", month: "long", year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="press-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" aria-label="Affra home">
            <Logo size="h-8" />
          </Link>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-indigo-600">
            <Newspaper className="w-3.5 h-3.5" />
            Press
          </div>
        </div>
      </header>

      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-6 py-14 lg:py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-indigo-600">
            / press releases
          </div>
          <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl leading-[1.02] tracking-tighter max-w-3xl">
            The story so far.
          </h1>
          <p className="mt-5 text-lg text-zinc-600 max-w-xl leading-relaxed">
            Announcements, milestones and background reading from the team building Affra.
            For interviews, quotes or high-res assets, email{" "}
            <a
              href="mailto:press@affra.io"
              className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
            >
              press@affra.io
            </a>
            .
          </p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-6 py-14 lg:py-20 space-y-10">
        {PRESS_RELEASES.length === 0 ? (
          <div className="text-sm text-zinc-500">No press releases yet — check back soon.</div>
        ) : (
          PRESS_RELEASES.map((r) => (
            <article
              key={r.slug}
              id={r.slug}
              data-testid={`press-card-${r.slug}`}
              className="rounded-2xl border border-zinc-200 bg-white p-8 lg:p-12 shadow-[0_1px_0_rgba(0,0,0,0.04)] scroll-mt-24"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-xs font-mono uppercase tracking-widest text-indigo-600">
                  For immediate release · {formatDate(r.publishedAt)}
                </div>
                <button
                  type="button"
                  onClick={() => copyAnchor(r.slug)}
                  data-testid={`press-copy-${r.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition"
                  title="Copy link to this release"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
              </div>

              <h2 className="mt-4 font-display font-black text-3xl lg:text-5xl leading-[1.05] tracking-tighter">
                {r.title}
              </h2>

              {r.subtitle && (
                <p className="mt-5 text-lg lg:text-xl text-zinc-700 leading-snug font-medium max-w-3xl">
                  {r.subtitle}
                </p>
              )}

              <div className="mt-8 press-body text-[17px] leading-[1.75] text-zinc-800">
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...p }) => (
                      <h3 className="mt-10 mb-3 font-display font-bold text-2xl tracking-tight" {...p} />
                    ),
                    h2: ({ node, ...p }) => (
                      <h4 className="mt-8 mb-3 font-display font-bold text-xl tracking-tight" {...p} />
                    ),
                    h3: ({ node, ...p }) => (
                      <h5 className="mt-6 mb-2 font-display font-bold text-lg tracking-tight" {...p} />
                    ),
                    p: ({ node, ...p }) => (
                      <p className="my-4 text-[17px] leading-[1.75]" {...p} />
                    ),
                    ul: ({ node, ...p }) => (
                      <ul className="my-4 pl-6 list-disc space-y-1.5" {...p} />
                    ),
                    ol: ({ node, ...p }) => (
                      <ol className="my-4 pl-6 list-decimal space-y-1.5" {...p} />
                    ),
                    blockquote: ({ node, ...p }) => (
                      <blockquote
                        className="my-6 pl-5 border-l-4 border-indigo-600 text-zinc-700 italic"
                        {...p}
                      />
                    ),
                    a: ({ node, ...p }) => (
                      <a
                        {...p}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
                      />
                    ),
                    code: ({ node, ...p }) => (
                      <code
                        className="px-1 py-0.5 rounded bg-zinc-100 text-[15px] font-mono"
                        {...p}
                      />
                    ),
                    hr: () => <hr className="my-10 border-zinc-200" />,
                  }}
                >
                  {r.body || ""}
                </ReactMarkdown>
              </div>

              <hr className="my-10 border-zinc-200" />
              <div className="text-sm text-zinc-600 leading-relaxed">
                <div className="font-display font-bold text-zinc-900 mb-2">Media contact</div>
                <a
                  href="mailto:press@affra.io"
                  className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700"
                >
                  <Mail className="w-3.5 h-3.5" /> press@affra.io
                </a>
                <p className="mt-4 text-zinc-500 text-xs">
                  Affra is a fully white-label ticketing platform for venues and promoters — 0% payment processing on every ticket and £0 monthly fees, forever.
                </p>
              </div>
            </article>
          ))
        )}
      </main>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} Affra</div>
          <Link to="/" className="hover:text-zinc-900">affra.io</Link>
        </div>
      </footer>
    </div>
  );
}
